import type { IPortfolioItem } from "../../mock-data/portfolioData";
import { createLogger } from '../Logger';
import { checkComplianceStatus, IComplianceStatusResult } from './PropertyHelpers';

import type { IProjectContract } from "../zeos-api/contracts/Project";
import type { IPropertyContract } from "../zeos-api/contracts/Property";
import type { IZeosApiService } from "../zeos-api/IZeosApiService";
export enum PortfolioModalType {
  None = "none",
  AddProperty = "addProperty",
  EditProperty = "editProperty",
  UploadImage = "uploadImage",
  UapiLink = "uapiLink",
  BayouLink = "bayouLink",
  DerapiLink = "derapiLink",
  Meters = "meters",
}

export const mapBuildingTypeToId = (
  buildingType: string
): number | undefined => {
  const mapping: Record<string, number> = {
    Office: 10,
    Retail: 11,
    Warehouse: 12,
    MultiFamily: 13,
    Industrial: 14,
    School: 15,
    Hospital: 16,
    Other: 1,
  };
  return mapping[buildingType] ?? undefined;
};

export type ProjectNumericFieldType =
  | "investment"
  | "paidBack"
  | "profitability"
  | "revenue";

export const projReducer = (
  projects: IProjectContract[],
  field: ProjectNumericFieldType
) =>
  projects.reduce((sum, proj) => sum + ((proj[field] ?? 0) as number), 0) ?? 0;

interface IComplianceMapParams {
  api: IZeosApiService;
  log: ReturnType<typeof createLogger>;
  properties: IPortfolioItem[];
}

export const buildComplianceStatusMap = async (
  params: IComplianceMapParams
): Promise<Map<number, IComplianceStatusResult>> => {
  const complianceMap = new Map<number, IComplianceStatusResult>();
  const propertyIds = Array.from(
    new Set(
      params.properties
        .map((item) => item.propertyId)
        .filter((propertyId): propertyId is number => Boolean(propertyId))
    )
  );

  if (propertyIds.length === 0) {
    return complianceMap;
  }

  const propertyResponse = await params.api.Property.getAllProperties(propertyIds);
  const propertyDetails = new Map<number, IPropertyContract>(
    (propertyResponse.data ?? [])
      .filter((property): property is IPropertyContract & { propertyId: number } =>
        Boolean(property?.propertyId)
      )
      .map((property) => [property.propertyId, property])
  );

  await Promise.all(
    params.properties.map(async (item) => {
      if (!item.propertyId) {
        return;
      }

      try {
        const propertyData = propertyDetails.get(item.propertyId);
        if (!propertyData) {
          return;
        }

        const result = await checkComplianceStatus({
          api: params.api,
          log: params.log,
          zip: propertyData.zip,
          bin: propertyData.bin,
          bbl: propertyData.bbl,
          propertyId: propertyData.propertyId ?? 0,
        });

        complianceMap.set(item.propertyId, result);
      } catch (error) {
        params.log.error(
          `Error checking compliance for property ${item.propertyId}:`,
          error
        );
      }
    })
  );

  return complianceMap;
};
